import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useTheme } from "../context/ThemeContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import DarkNewsletterBackgroundCover from "../images/newsletter_cover.jpg";
import LightDesktopNewsletterBgCover from "../images/newsletter/Newsletter_background_image_people_desktop.png";
import LightMobileNewsletterBgCover from "../images/newsletter/Newsletter_background_people.png";
import { processForm } from "../utils/processForm";
import { StatusComp } from "./shared/statusComp";
import CtaButton from "./shared/buttons/ctaButton";

const Newsletter = ({ headerCol, bgSrc, headerColTitleClass }) => {
  const { colorMode, currentBrand } = useTheme();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  const [status, setStatus] = useState("");
  const [newletterBgImage, setNewsletterBgImage] = useState(
    DarkNewsletterBackgroundCover
  );

  const breakpoint = useBreakpoint();

  const onSubmit = async (data) => {
    const formFields = {
      form_type: "newsletter_form",
      newsletter: "newsletter_signup",
      ...data
    };

    setStatus("processing");
    await processForm({
      formdata: formFields,
      currentBrand
    });

    setStatus("success");
    reset();
  };

  const expTexts = {
    processing: "Hang tight!",
    success: "Amazing!"
  };

  const statusTexts = {
    processing: "Your email will reach us soon...",
    success: "You are now subscribed to our weekly newsletter."
  };

  useEffect(() => {
    setNewsletterBgImage(
      breakpoint === "xs" ? bgSrc.light.mobile : bgSrc.light.desktop
    );
  }, [breakpoint, colorMode, bgSrc]);

  return (
    <>
      <section className="newsletter-section bg-green-light flex flex-col items-center justify-center mx-auto relative z-10 pb-36 sm:pb-0 !min-h-0">
        <div
          className={`w-full z-0 h-[27rem] sm:h-[32rem] bg-white bg-cover bg-no-repeat bg-center relative overflow-hidden before:content-[''] before:absolute before:inset-0 before:block before:bg-green-dark/75 before:z-[-5]`}
          style={{
            backgroundImage: `url('${newletterBgImage}')`
          }}
        />

        <div className="w-11/12 sm:w-full absolute z-10 sm:top-0 flex flex-col items-center">
          <div className="relative flex flex-col md:w-11/12 xl:w-8/12">
            <div
              className={`sm:absolute sm:-translate-y-1/2 flex flex-col justify-center w-full mx-auto py-2 pb-12 md:pb-0 h-24 ${headerCol}`}
            >
              <h1
                className={headerColTitleClass}
                dangerouslySetInnerHTML={{
                  __html: "Subscribe to our newsletter"
                }}
              />
            </div>

            <div className="hidden flex-col justify-center pt-24 pb-12 sm:flex">
              <div
                className="text-center text-white text-med-sm font-light"
                dangerouslySetInnerHTML={{
                  __html:
                    "The best of World of Difference programs, news & offers delivered to your inbox."
                }}
              />
              <h6
                className="font-semi-bold text-center text-white"
                dangerouslySetInnerHTML={{
                  __html:
                    "Sign up for inspiring stories, news and special offers from World of Difference"
                }}
              />
            </div>

            <div className="flex flex-row justify-between items-center w-full mx-auto sm:py-4 sm:px-7 gvi-border-light">
              <div className="flex justify-between items-center w-full mx-auto gvi-blur-bg gvi-border-light border rounded-xxs flex-row">
                {!status ? (
                  <form
                    id={"newsletter"}
                    className="flex justify-between items-center flex-wrap w-full py-8 px-7 flex-col rounded-xxs sm:py-9 sm:px-16 sm:flex-row"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <label className="w-full sm:w-3/4">
                      <input
                        {...register("email_address", {
                          required: true,
                          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                        })}
                        placeholder="Enter your email address"
                        className={`w-full bg-transparent gvi-border-light border p-4 text-mob focus:outline-0 font-bold rounded-xxs text-center text-white placeholder:text-white sm:px-6 sm:py-2 sm:text-left ${
                          errors.email_address ? " !border-red-600 " : ""
                        }`}
                      />
                    </label>
                    <div className="button-submit flex w-full justify-end mt-2 sm:w-1/4 sm:pl-6 sm:mt-0">
                      <CtaButton
                        ctaLabel="Subscribe"
                        preset="tertiary"
                        type="submit"
                        extraButtonStyle="w-full"
                        showAsButton={true}
                        showArrowIcon={false}
                      />
                    </div>
                  </form>
                ) : (
                  <div className="py-5 w-full">
                    <StatusComp
                      expText={expTexts[status]}
                      statusText={statusTexts[status]}
                      status={status}
                      align="center"
                      txtColor="text-white"
                      iconColor="text-white fill-white"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col justify-center absolute sm:static -bottom-44 sm:bottom-0">
              <div
                className="font-semibold h8 !text-white sm:text-center"
                dangerouslySetInnerHTML={{
                  __html:
                    "By signing up for this email, you are agreeing to news, offers, and information from World of Difference and our partners."
                }}
              />

              <div className="font-semibold h8 !text-white sm:text-center">
                Click here to visit our{" "}
                <a
                  className="underline text-white hover:text-green-dark hover:md:text-green-light"
                  href="/privacy"
                  target="_blank"
                >
                  Privacy Policy
                </a>
                . Easy unsubscribe links are provided in every email.
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

Newsletter.defaultProps = {
  headerCol: "sm:bg-white",
  headerColTitleClass:
    "sm:text-green-dark text-white text-title bg-transparent text-left uppercase sm:text-center sm:text-newsletter-title",
  bgSrc: {
    light: {
      desktop: LightDesktopNewsletterBgCover,
      mobile: LightMobileNewsletterBgCover
    },
    dark: {
      desktop: DarkNewsletterBackgroundCover,
      mobile: DarkNewsletterBackgroundCover
    }
  }
};

const bgPerDeviceShape = {
  desktop: PropTypes.string,
  mobile: PropTypes.string
};

Newsletter.propTypes = {
  headerCol: PropTypes.string,
  bgSrc: PropTypes.shape({
    light: PropTypes.shape(bgPerDeviceShape),
    dark: PropTypes.shape(bgPerDeviceShape)
  })
};

export default Newsletter;
